import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Repeat } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi } from '@/hooks/useApi';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { madridDay, Panel, RangoSelector, Stat } from '@/components/aperturas/shared';
import { useCatalogosDispositivos } from '@/components/dispositivos/catalogos';
import { fechaCorta, GRAVEDAD_LABEL, TIPO_LABEL, type ResumenIncidencias } from '@/lib/dispositivos';

/**
 * El registro global de incidencias en un rango, y lo que se saca de él:
 * quién repite, qué modelos fallan más (por unidad y mes, con el número de
 * unidades al lado) y qué lotes (año de compra). No es una bandeja de
 * tickets: nada se cierra aquí; se lee y se decide qué comprar y qué no
 * volver a instalar.
 */
export default function DispositivosIncidenciasPage() {
  const { catalogos } = useCatalogosDispositivos();
  const [days, setDays] = useState(90);
  const [tipo, setTipo] = useState('');
  const [origen, setOrigen] = useState('');

  const { data, loading, error } = useApi<ResumenIncidencias>(
    () => api.getDispositivosIncidencias({ desde: madridDay(days - 1), hasta: madridDay(), tipo, origen }),
    [days, tipo, origen],
  );

  const etiquetaTipo = (id: string) => catalogos.incidenciaLabel[id] || id;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-sereno-dark/50">
          Un registro, no una bandeja: lo que ha fallado, a mano y detectado por el barrido. Para saber qué no volver a instalar y qué comprar.
        </p>
        <RangoSelector days={days} onChange={setDays} />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <select className="input w-auto" value={tipo} onChange={(e) => setTipo(e.target.value)}>
          <option value="">Todos los tipos</option>
          {catalogos.incidenciaTipos.map((t) => (
            <option key={t.id} value={t.id}>{t.label}</option>
          ))}
        </select>
        <select className="input w-auto" value={origen} onChange={(e) => setOrigen(e.target.value)}>
          <option value="">Manuales y automáticas</option>
          <option value="manual">Solo manuales</option>
          <option value="auto">Solo automáticas</option>
        </select>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : error || !data ? (
        <EmptyState title="No se han podido cargar las incidencias" description={error ?? undefined} />
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label="Incidencias" value={data.total} />
            <Stat label="Manuales" value={data.manuales} />
            <Stat label="Automáticas" value={data.automaticas} hint="del barrido diario" />
            <Stat label="Bloquean el acceso" value={data.bloquean} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Panel title="Reincidentes" hint={`Dos o más incidencias en ${days} días.`}>
              {data.reincidentes.length === 0 ? (
                <p className="text-sm text-slate-400">Nadie repite en el rango.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {data.reincidentes.map((r) => (
                    <li key={r.dispositivoId} className="flex items-start gap-2">
                      <Repeat size={14} className="text-red-500 mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <Link to={`/dispositivos/${r.dispositivoId}`} className="font-mono font-medium hover:underline">{r.idFabricante}</Link>
                        <span className="text-slate-500"> · {TIPO_LABEL[r.tipoDispositivo]}{r.casa ? ` · ${r.casa}` : ''}</span>
                        <p className="text-xs text-slate-500">
                          {r.incidencias} incidencias · última {fechaCorta(r.ultima)} · {r.tipos.map(etiquetaTipo).join(', ')}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>

            <Panel title="Por modelo" hint="Incidencias por unidad y mes. Con pocas unidades, el número dice poco.">
              <Tabla
                cabeceras={['Modelo', 'Uds.', 'Inc.', '/ ud·mes']}
                filas={data.porModelo.map((m) => [
                  <span key="m">{m.etiqueta}{m.tipoDispositivo ? <span className="text-slate-400 text-xs"> · {TIPO_LABEL[m.tipoDispositivo]}</span> : null}</span>,
                  m.unidades,
                  m.incidencias,
                  <span key="t" className={m.porUnidadMes > 0 ? 'font-medium' : 'text-slate-400'}>{m.porUnidadMes.toLocaleString('es-ES')}</span>,
                ])}
              />
            </Panel>

            <Panel title="Por lote" hint="Por año de compra; sin fecha de compra, un lote aparte.">
              <Tabla
                cabeceras={['Lote', 'Uds.', 'Inc.', '/ ud·mes']}
                filas={data.porLote.map((l) => [l.lote, l.unidades, l.incidencias, l.porUnidadMes.toLocaleString('es-ES')])}
              />
            </Panel>
          </div>

          <Panel title="Registro">
            {data.filas.length === 0 ? (
              <p className="text-sm text-slate-400">Sin incidencias en el rango.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-slate-500">
                    <tr>
                      <th className="text-left font-medium pb-2 pr-3">Fecha</th>
                      <th className="text-left font-medium pb-2 pr-3">Dispositivo</th>
                      <th className="text-left font-medium pb-2 pr-3">Casa</th>
                      <th className="text-left font-medium pb-2 pr-3">Tipo</th>
                      <th className="text-left font-medium pb-2">Qué pasó</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.filas.map((f) => (
                      <tr key={f.id} className="border-t border-slate-100 align-top">
                        <td className="py-2 pr-3 whitespace-nowrap text-slate-500 text-xs">{fechaCorta(f.fecha)}</td>
                        <td className="py-2 pr-3">
                          <Link to={`/dispositivos/${f.dispositivoId}?tab=incidencias`} className="font-mono hover:underline">{f.idFabricante}</Link>
                          <span className="text-slate-400 text-xs"> {TIPO_LABEL[f.tipoDispositivo]}</span>
                        </td>
                        <td className="py-2 pr-3 text-slate-600">{f.casa ?? <span className="text-slate-300">—</span>}</td>
                        <td className="py-2 pr-3 whitespace-nowrap">
                          {etiquetaTipo(f.incidenciaTipo)}
                          {f.gravedad === 'bloquea_acceso' && (
                            <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] text-red-700 bg-red-100 px-1.5 py-0.5 rounded" title={GRAVEDAD_LABEL[f.gravedad]}>
                              <AlertTriangle size={10} /> bloquea
                            </span>
                          )}
                          {f.origen === 'auto' && <span className="ml-1 text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">auto</span>}
                          {f.ocurrencias && f.ocurrencias > 1 ? <span className="ml-1 text-xs text-slate-400">×{f.ocurrencias}</span> : null}
                        </td>
                        <td className="py-2 text-slate-600 max-w-md">
                          <span className="line-clamp-2" title={f.descripcion}>{f.descripcion}</span>
                          {f.queSeHizo && <span className="block text-xs text-slate-400">Qué se hizo: {f.queSeHizo}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}

function Tabla({ cabeceras, filas }: { cabeceras: string[]; filas: React.ReactNode[][] }) {
  if (filas.length === 0) return <p className="text-sm text-slate-400">Nada que contar.</p>;
  return (
    <table className="w-full text-sm">
      <thead className="text-slate-500">
        <tr>
          {cabeceras.map((c, i) => (
            <th key={c} className={`font-medium pb-2 ${i === 0 ? 'text-left' : 'text-right'}`}>{c}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {filas.map((f, i) => (
          <tr key={i} className="border-t border-slate-100">
            {f.map((c, j) => (
              <td key={j} className={`py-1.5 ${j === 0 ? 'text-left' : 'text-right tabular-nums'}`}>{c}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
